<template>
  <div class="lc">
    <Head>
      <title>Catálogo Digital con pedidos por WhatsApp por solo ${{ price }} - Realgestión</title>
      <meta name="description" :content="`Crea tu tienda online en minutos: un link con todos tus productos, precios y fotos. Tus clientes arman su carrito y el pedido llega a tu WhatsApp. Accede por solo $${price}.`" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://realgestion.lat/landing-catalogo" />
      <meta property="og:title" :content="`Catálogo Digital con pedidos por WhatsApp — Solo $${price}`" />
      <meta property="og:description" content="Un solo link con todos tus productos. Los pedidos llegan ordenados a tu WhatsApp y quedan registrados en tu panel." />
      <meta property="og:image" content="https://realgestion.lat/images/og-image.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <link rel="canonical" href="https://realgestion.lat/landing-catalogo" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@700;800&display=swap" rel="stylesheet" />
    </Head>

    <!-- ───────────── Header minimalista (funnel: sin distracciones) ───────────── -->
    <header class="lc-nav" :class="{ 'is-scrolled': scrolled }">
      <div class="lc-container lc-nav__inner">
        <a href="/" class="lc-nav__brand" aria-label="Realgestión inicio">
          <img src="/logo.png" alt="Realgestión" />
        </a>
        <nav class="lc-nav__links" aria-label="Secciones">
          <a href="#funciones">Funciones</a>
          <a href="#como-funciona">Cómo funciona</a>
          <a href="#precio">Precio</a>
          <a href="#faq">Preguntas</a>
        </nav>
        <a id="cta-nav-whatsapp" :href="waLink('nav')" target="_blank" rel="noopener" class="lc-btn lc-btn--wa lc-btn--sm" @click="track('nav')">
          <i class="fab fa-whatsapp" /> <span>Escríbenos</span>
        </a>
      </div>
    </header>

    <main>
      <!-- ───────────── HERO ───────────── -->
      <section class="lc-hero">
        <div class="lc-hero__bg" aria-hidden="true">
          <span class="lc-glow lc-glow--orange" />
          <span class="lc-glow lc-glow--green" />
          <span class="lc-hero__grid-pattern" />
        </div>

        <div class="lc-container lc-hero__grid">
          <div class="lc-hero__copy">
            <span class="lc-pill"><span class="lc-pill__dot" /> Nuevo · Catálogo Digital de Realgestión</span>

            <h1 class="lc-hero__title">
              Tu tienda online lista hoy.
              <span class="lc-grad">Los pedidos llegan a tu WhatsApp.</span>
            </h1>

            <p class="lc-hero__sub">
              Deja de enviar fotos una por una y de responder <em>“¿precio?”</em> todo el día.
              Comparte <strong>un solo link</strong> con todos tus productos, fotos y precios, y recibe pedidos ordenados directo en tu WhatsApp.
            </p>

            <div class="lc-hero__price">
              <span class="lc-hero__price-label">Accede por solo</span>
              <span class="lc-hero__price-value">${{ price }}</span>
              <span class="lc-hero__price-note">Sin comisiones por venta</span>
            </div>

            <div class="lc-hero__ctas">
              <a id="cta-hero-whatsapp" :href="waLink('hero')" target="_blank" rel="noopener" class="lc-btn lc-btn--wa lc-btn--lg lc-btn--pulse" @click="track('hero')">
                <i class="fab fa-whatsapp" /> Quiero mi catálogo por ${{ price }}
              </a>
              <a id="cta-hero-how" href="#como-funciona" class="lc-btn lc-btn--ghost lc-btn--lg">
                Ver cómo funciona <i class="fas fa-arrow-down" />
              </a>
            </div>

            <ul class="lc-hero__trust">
              <li><i class="fas fa-check-circle" /> Sin saber de tecnología</li>
              <li><i class="fas fa-check-circle" /> Sin página web ni dominio</li>
              <li><i class="fas fa-check-circle" /> Soporte por WhatsApp</li>
            </ul>
          </div>

          <!-- Mockup del teléfono con un catálogo -->
          <div class="lc-hero__visual" aria-hidden="true">
            <div class="lc-phone">
              <div class="lc-phone__notch" />
              <div class="lc-phone__screen">
                <div class="lc-mock__bar">
                  <div class="lc-mock__store">
                    <span class="lc-mock__logo">MB</span>
                    <div>
                      <strong>Moda Bella</strong>
                      <small>realgestion.lat/catalogo/modabella</small>
                    </div>
                  </div>
                  <span class="lc-mock__bag"><i class="fas fa-shopping-bag" /><b>2</b></span>
                </div>
                <div class="lc-mock__banner">
                  <span>🔥 Nueva colección</span>
                  <strong>Hasta 30% OFF</strong>
                </div>
                <div class="lc-mock__search"><i class="fas fa-search" /> Buscar productos…</div>
                <div class="lc-mock__chips">
                  <span class="is-active">Todos</span><span>Vestidos</span><span>Calzado</span><span>Ofertas</span>
                </div>
                <div class="lc-mock__products">
                  <div v-for="p in mockProducts" :key="p.name" class="lc-mock__card">
                    <div class="lc-mock__img" :style="{ background: p.bg }">
                      <span>{{ p.emoji }}</span>
                      <em v-if="p.tag" :class="`lc-mock__tag lc-mock__tag--${p.tagType}`">{{ p.tag }}</em>
                    </div>
                    <div class="lc-mock__info">
                      <small>{{ p.name }}</small>
                      <div><b>${{ p.price }}</b><i class="fas fa-plus" /></div>
                    </div>
                  </div>
                </div>
                <div class="lc-mock__checkout"><i class="fab fa-whatsapp" /> Pedir por WhatsApp · $55.00</div>
              </div>
            </div>

            <div class="lc-float lc-float--order">
              <span class="lc-float__icon lc-float__icon--wa"><i class="fab fa-whatsapp" /></span>
              <div>
                <strong>Nuevo pedido #1024</strong>
                <small>María G. · 2 productos · $55.00</small>
              </div>
            </div>
            <div class="lc-float lc-float--stock">
              <span class="lc-float__icon lc-float__icon--orange"><i class="fas fa-clipboard-check" /></span>
              <div>
                <strong>Orden registrada</strong>
                <small>Inventario actualizado</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ───────────── Marquee: Ideal para ───────────── -->
      <section class="lc-marquee" aria-label="Tipos de negocio">
        <p class="lc-marquee__label">Ideal para</p>
        <div class="lc-marquee__track">
          <ul class="lc-marquee__list">
            <li v-for="b in businesses" :key="b.label"><span>{{ b.emoji }}</span> {{ b.label }}</li>
          </ul>
          <ul class="lc-marquee__list" aria-hidden="true">
            <li v-for="b in businesses" :key="'dup-' + b.label"><span>{{ b.emoji }}</span> {{ b.label }}</li>
          </ul>
        </div>
      </section>

      <!-- ───────────── Problema → Solución ───────────── -->
      <section class="lc-section">
        <div class="lc-container">
          <header class="lc-heading lc-reveal">
            <span class="lc-eyebrow">¿Vendes por WhatsApp o Instagram?</span>
            <h2>Seguro esto te suena familiar…</h2>
            <p>Vender por chat funciona, pero te roba horas cada día. El Catálogo Digital ordena tus ventas sin cambiar la forma en que tus clientes te compran.</p>
          </header>

          <div class="lc-compare">
            <article class="lc-compare__card lc-compare__card--before lc-reveal">
              <h3><i class="fas fa-times-circle" /> Vendiendo solo por chat</h3>
              <ul>
                <li v-for="item in painPoints" :key="item"><i class="fas fa-times" /> {{ item }}</li>
              </ul>
            </article>
            <div class="lc-compare__arrow" aria-hidden="true"><i class="fas fa-arrow-right" /></div>
            <article class="lc-compare__card lc-compare__card--after lc-reveal">
              <h3><i class="fas fa-check-circle" /> Con tu Catálogo Digital</h3>
              <ul>
                <li v-for="item in gains" :key="item"><i class="fas fa-check" /> {{ item }}</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <!-- ───────────── Cómo funciona ───────────── -->
      <section id="como-funciona" class="lc-section lc-section--tint">
        <div class="lc-container">
          <header class="lc-heading lc-reveal">
            <span class="lc-eyebrow">Así de fácil</span>
            <h2>Empieza a vender en 3 pasos</h2>
            <p>Sin programadores, sin hosting y sin complicaciones.</p>
          </header>

          <ol class="lc-steps">
            <li v-for="(step, i) in steps" :key="step.title" class="lc-step lc-reveal">
              <span class="lc-step__num">{{ i + 1 }}</span>
              <span class="lc-step__icon"><i :class="step.icon" /></span>
              <h3>{{ step.title }}</h3>
              <p>{{ step.text }}</p>
            </li>
          </ol>
        </div>
      </section>

      <!-- ───────────── Funciones ───────────── -->
      <section id="funciones" class="lc-section">
        <div class="lc-container">
          <header class="lc-heading lc-reveal">
            <span class="lc-eyebrow">Todo incluido</span>
            <h2>Todo lo que necesitas para vender online</h2>
            <p>Un catálogo profesional conectado a tu inventario y a tu panel de Realgestión.</p>
          </header>

          <div class="lc-features">
            <article v-for="f in features" :key="f.title" class="lc-feature lc-reveal">
              <span class="lc-feature__icon" :class="`lc-feature__icon--${f.color}`"><i :class="f.icon" /></span>
              <h3>{{ f.title }}</h3>
              <p>{{ f.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <!-- ───────────── Demo del pedido en WhatsApp ───────────── -->
      <section class="lc-section lc-section--dark">
        <div class="lc-container lc-demo">
          <div class="lc-demo__copy lc-reveal">
            <span class="lc-eyebrow lc-eyebrow--light">Pedidos sin confusiones</span>
            <h2>Así te llega cada pedido a tu WhatsApp</h2>
            <p>Tu cliente elige sus productos, escribe su nombre y dirección, y con un toque te envía un resumen claro. Nada de mensajes sueltos ni capturas de pantalla.</p>
            <ul class="lc-demo__list">
              <li><i class="fas fa-check" /> Productos, cantidades y total calculado</li>
              <li><i class="fas fa-check" /> Datos del cliente y dirección de entrega</li>
              <li><i class="fas fa-check" /> Número de pedido registrado en tu panel</li>
            </ul>
            <a id="cta-demo-whatsapp" :href="waLink('demo')" target="_blank" rel="noopener" class="lc-btn lc-btn--wa lc-btn--lg" @click="track('demo')">
              <i class="fab fa-whatsapp" /> Quiero recibir pedidos así
            </a>
          </div>

          <div class="lc-chat lc-reveal" aria-label="Ejemplo de pedido recibido por WhatsApp">
            <div class="lc-chat__head">
              <span class="lc-chat__avatar"><i class="fas fa-user" /></span>
              <div>
                <strong>María González</strong>
                <small>en línea</small>
              </div>
            </div>
            <div class="lc-chat__body">
              <div class="lc-chat__bubble">
                <p>🛍️ <strong>Nuevo Pedido — Moda Bella</strong></p>
                <p>👤 Cliente: María González<br />📱 WhatsApp: +58 412 555 0000<br />📍 Dirección: Av. Bolívar, Valencia<br />🔖 Pedido #1024</p>
                <p class="lc-chat__sep">─────────────────</p>
                <p><strong>Productos:</strong><br />• 1x Vestido Floral — $25.00<br />• 1x Cartera Classic — $30.00</p>
                <p class="lc-chat__sep">─────────────────</p>
                <p>💰 <strong>Total: $55.00</strong></p>
                <p>¡Gracias por tu pedido! 🎉</p>
                <span class="lc-chat__time">10:24 a. m. <i class="fas fa-check-double" /></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ───────────── Precio ───────────── -->
      <section id="precio" class="lc-section lc-section--tint">
        <div class="lc-container">
          <header class="lc-heading lc-reveal">
            <span class="lc-eyebrow">Precio simple</span>
            <h2>Tu tienda online por menos de lo que cuesta un almuerzo</h2>
            <p>Un solo precio, todo incluido. Sin comisiones por tus ventas.</p>
          </header>

          <div class="lc-pricing lc-reveal">
            <div class="lc-pricing__badge">Oferta de lanzamiento</div>
            <div class="lc-pricing__top">
              <h3>Catálogo Digital</h3>
              <p class="lc-pricing__amount"><span class="lc-pricing__currency">$</span>{{ price }}</p>
              <p class="lc-pricing__caption">para acceder a tu catálogo</p>
              <p class="lc-pricing__anchor">Plataformas similares cobran entre <s>$12 y $35</s> por sus planes</p>
            </div>
            <ul class="lc-pricing__list">
              <li v-for="item in included" :key="item"><i class="fas fa-check" /> {{ item }}</li>
            </ul>
            <a id="cta-pricing-whatsapp" :href="waLink('pricing')" target="_blank" rel="noopener" class="lc-btn lc-btn--wa lc-btn--lg lc-btn--block" @click="track('pricing')">
              <i class="fab fa-whatsapp" /> Activar mi catálogo por ${{ price }}
            </a>
            <p class="lc-pricing__foot"><i class="fas fa-lock" /> Te atendemos por WhatsApp y te guiamos en la activación</p>
          </div>
        </div>
      </section>

      <!-- ───────────── FAQ ───────────── -->
      <section id="faq" class="lc-section">
        <div class="lc-container lc-container--narrow">
          <header class="lc-heading lc-reveal">
            <span class="lc-eyebrow">Resolvemos tus dudas</span>
            <h2>Preguntas frecuentes</h2>
          </header>

          <div class="lc-faq">
            <details v-for="(q, i) in faqs" :key="q.q" class="lc-faq__item" name="lc-faq" :open="i === 0">
              <summary>{{ q.q }}<i class="fas fa-plus" aria-hidden="true" /></summary>
              <p>{{ q.a }}</p>
            </details>
          </div>

          <p class="lc-faq__more">
            ¿Tienes otra pregunta?
            <a id="cta-faq-whatsapp" :href="waLink('faq')" target="_blank" rel="noopener" @click="track('faq')">Escríbenos por WhatsApp <i class="fas fa-arrow-right" /></a>
          </p>
        </div>
      </section>

      <!-- ───────────── CTA final ───────────── -->
      <section class="lc-final">
        <div class="lc-container lc-final__inner lc-reveal">
          <h2>Tus clientes ya están en WhatsApp.<br /><span class="lc-grad">Dales una tienda donde comprarte.</span></h2>
          <p>Activa hoy tu Catálogo Digital por solo <strong>${{ price }}</strong> y empieza a recibir pedidos ordenados.</p>
          <a id="cta-final-whatsapp" :href="waLink('final')" target="_blank" rel="noopener" class="lc-btn lc-btn--wa lc-btn--xl lc-btn--pulse" @click="track('final')">
            <i class="fab fa-whatsapp" /> Hablar por WhatsApp ahora
          </a>
          <small class="lc-final__phone">+58 424-401-0195</small>
        </div>
      </section>
    </main>

    <footer class="lc-footer">
      <div class="lc-container lc-footer__inner">
        <img src="/logo.png" alt="Realgestión" class="lc-footer__logo" />
        <p>&copy; {{ year }} Realgestión. Hecho con <span>♥</span> en Venezuela.</p>
        <nav aria-label="Legal">
          <a href="/terms">Términos</a>
          <a href="/privacy">Privacidad</a>
        </nav>
      </div>
    </footer>

    <!-- Botón flotante (desktop) -->
    <a id="cta-floating-whatsapp" :href="waLink('floating')" target="_blank" rel="noopener" class="lc-fab" aria-label="Escríbenos por WhatsApp" @click="track('floating')">
      <i class="fab fa-whatsapp" />
    </a>

    <!-- Barra fija (mobile) -->
    <div class="lc-mobilebar" :class="{ 'is-visible': scrolled }">
      <div>
        <small>Catálogo Digital</small>
        <strong>Solo ${{ price }}</strong>
      </div>
      <a id="cta-mobilebar-whatsapp" :href="waLink('mobilebar')" target="_blank" rel="noopener" class="lc-btn lc-btn--wa" @click="track('mobilebar')">
        <i class="fab fa-whatsapp" /> Lo quiero
      </a>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'

export default {
  components: { Head },

  props: {
    price: { type: Number, default: 10 },
    whatsapp: { type: String, default: '584244010195' },
  },

  data() {
    return {
      scrolled: false,
      year: new Date().getFullYear(),

      mockProducts: [
        { name: 'Vestido Floral', price: 25, emoji: '👗', bg: 'linear-gradient(135deg,#fde68a,#fca5a5)', tag: '-30%', tagType: 'offer' },
        { name: 'Zapatos Urban', price: 40, emoji: '👟', bg: 'linear-gradient(135deg,#bfdbfe,#c7d2fe)', tag: 'Top', tagType: 'featured' },
        { name: 'Cartera Classic', price: 30, emoji: '👜', bg: 'linear-gradient(135deg,#fed7aa,#fecdd3)' },
        { name: 'Lentes Sun', price: 15, emoji: '🕶️', bg: 'linear-gradient(135deg,#bbf7d0,#a5f3fc)' },
      ],

      businesses: [
        { emoji: '👗', label: 'Tiendas de ropa' },
        { emoji: '🍔', label: 'Restaurantes y comida' },
        { emoji: '🛒', label: 'Bodegones' },
        { emoji: '💄', label: 'Cosméticos' },
        { emoji: '🔧', label: 'Ferreterías' },
        { emoji: '🚗', label: 'Repuestos' },
        { emoji: '📱', label: 'Tecnología' },
        { emoji: '🥐', label: 'Panaderías' },
        { emoji: '👟', label: 'Calzado' },
        { emoji: '🎁', label: 'Emprendedores' },
      ],

      painPoints: [
        'Envías fotos una por una a cada cliente',
        'Respondes “¿precio?” y “¿hay talla?” todo el día',
        'Los pedidos se pierden entre cientos de chats',
        'Vendes algo que ya no tienes en inventario',
        'Tu negocio se ve improvisado frente a la competencia',
      ],

      gains: [
        'Un solo link con todos tus productos y precios',
        'El cliente ve fotos, precios y disponibilidad solo',
        'Pedidos ordenados con productos, cantidades y total',
        'Disponibilidad conectada a tu inventario real',
        'Una tienda profesional con los colores de tu marca',
      ],

      steps: [
        { icon: 'fab fa-whatsapp', title: 'Activa tu catálogo', text: `Escríbenos por WhatsApp, realiza el pago de $${this.price} y activamos tu acceso.` },
        { icon: 'fas fa-box-open', title: 'Carga tus productos', text: 'Sube fotos, precios y categorías desde tu panel. Personaliza colores, logo y banners.' },
        { icon: 'fas fa-share-alt', title: 'Comparte y vende', text: 'Publica tu link en Instagram, estados y grupos. Los pedidos llegan directo a tu WhatsApp.' },
      ],

      features: [
        { icon: 'fas fa-link', color: 'orange', title: 'Tu propio link', text: 'Una dirección para tu tienda, lista para la bio de Instagram, estados y grupos de WhatsApp.' },
        { icon: 'fab fa-whatsapp', color: 'green', title: 'Pedidos por WhatsApp', text: 'Carrito de compras que envía a tu WhatsApp un resumen con productos, cantidades y total.' },
        { icon: 'fas fa-clipboard-check', color: 'blue', title: 'Pedidos registrados', text: 'Cada pedido se guarda automáticamente como orden en tu panel de Realgestión.' },
        { icon: 'fas fa-boxes', color: 'purple', title: 'Inventario conectado', text: 'Tus clientes ven “En stock” o “Agotado” según tu inventario real. Adiós a vender lo que no tienes.' },
        { icon: 'fas fa-palette', color: 'pink', title: 'Con tu marca', text: 'Tu logo y tus colores: principal, fondo, tarjetas, ofertas y textos.' },
        { icon: 'fas fa-images', color: 'amber', title: 'Banners promocionales', text: 'Slides rotativos en la cabecera para destacar ofertas, lanzamientos y temporadas.' },
        { icon: 'fas fa-tags', color: 'orange', title: 'Categorías y ofertas', text: 'Organiza por categorías y resalta productos en oferta o destacados.' },
        { icon: 'fas fa-search', color: 'blue', title: 'Buscador integrado', text: 'Tus clientes encuentran lo que buscan por nombre, código o descripción.' },
        { icon: 'fas fa-truck', color: 'purple', title: 'Delivery o retiro', text: 'El cliente indica su dirección de entrega o retira en tu tienda.' },
        { icon: 'fas fa-address-card', color: 'pink', title: 'Contacto y redes', text: 'Teléfono, correo, dirección, horario e Instagram/Facebook visibles en tu catálogo.' },
        { icon: 'fas fa-mobile-alt', color: 'amber', title: 'Hecho para celular', text: 'Rápido y cómodo desde cualquier teléfono. Tus clientes no descargan ninguna app.' },
      ],

      included: [
        'Catálogo digital con link propio',
        'Carrito y pedidos directo a tu WhatsApp',
        'Pedidos guardados en tu panel',
        'Stock conectado a tu inventario',
        'Personalización con tu logo y colores',
        'Banners, categorías, ofertas y cupones',
        'Sin comisiones por tus ventas',
      ],

      faqs: [
        { q: `¿Qué incluye el acceso de $${this.price}?`, a: 'Tu catálogo digital con link propio, carrito de compras, pedidos por WhatsApp, personalización con tu marca, banners, categorías, cupones y el registro automático de cada pedido en tu panel de Realgestión.' },
        { q: '¿Necesito saber de tecnología o tener una página web?', a: 'No. Cargas tus productos desde tu panel y compartes el link. No necesitas dominio, hosting ni programador. Si tienes dudas, te guiamos por WhatsApp.' },
        { q: '¿Cobran comisión por cada venta?', a: 'No. Los pedidos llegan directo a tu WhatsApp y el pago lo acuerdas con tu cliente. Realgestión no cobra comisión por tus ventas.' },
        { q: '¿Mis clientes tienen que descargar una app o registrarse?', a: 'No. Abren tu link desde cualquier celular o computadora, eligen sus productos y te envían el pedido por WhatsApp.' },
        { q: '¿Puedo cambiar precios y productos cuando quiera?', a: 'Sí. Todo se administra desde tu panel y los cambios se reflejan de inmediato en tu catálogo.' },
        { q: `¿Cómo pago los $${this.price}?`, a: 'Escríbenos por WhatsApp y te indicamos los métodos de pago disponibles y los pasos para activar tu catálogo.' },
        { q: '¿Para qué tipo de negocios sirve?', a: 'Tiendas de ropa y calzado, comida, bodegones, cosméticos, ferreterías, repuestos, tecnología y cualquier negocio que venda productos.' },
      ],
    }
  },

  computed: {
    waMessages() {
      return {
        nav: 'Hola 👋 Vi la página del Catálogo Digital de Realgestión y quiero más información.',
        hero: `Hola 👋 Quiero activar mi Catálogo Digital de Realgestión por $${this.price}.`,
        demo: 'Hola 👋 Quiero que los pedidos de mis clientes me lleguen organizados por WhatsApp con el Catálogo Digital.',
        pricing: `Hola 👋 Quiero contratar el Catálogo Digital por $${this.price}. ¿Cómo hago el pago?`,
        faq: 'Hola 👋 Tengo unas preguntas sobre el Catálogo Digital de Realgestión.',
        final: `Hola 👋 Estoy listo para activar mi Catálogo Digital por $${this.price}.`,
        floating: 'Hola 👋 Quiero información del Catálogo Digital de Realgestión.',
        mobilebar: `Hola 👋 Quiero mi Catálogo Digital por $${this.price}.`,
      }
    },
  },

  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
  },

  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 40
    },

    waLink(source) {
      const text = this.waMessages[source] || this.waMessages.floating
      return `https://wa.me/${this.whatsapp}?text=${encodeURIComponent(text)}`
    },

    // Registra el clic como conversión si hay píxeles de analítica cargados
    track(source) {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Contact', { content_name: 'landing-catalogo', source })
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'whatsapp_click', { page: 'landing-catalogo', source })
      }
    },
  },
}
</script>

<style scoped>
/* ════════════ Tokens ════════════ */
.lc {
  --ink: #0b1020;
  --ink-2: #131b31;
  --text: #0f172a;
  --muted: #5b6475;
  --line: #e9e7e1;
  --bg: #fafaf7;
  --tint: #fff7ed;
  --orange: #f97316;
  --orange-2: #ea580c;
  --amber: #fbbf24;
  --wa: #25d366;
  --wa-2: #1ebe5b;
  --wa-dark: #075e54;
  --radius: 20px;
  --shadow-lg: 0 30px 80px -20px rgba(15, 23, 42, 0.35);
  --shadow-md: 0 12px 32px -12px rgba(15, 23, 42, 0.18);

  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--text);
  background: var(--bg);
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}

.lc-container { width: 100%; max-width: 1200px; margin: 0 auto; padding: 0 20px; }
.lc-container--narrow { max-width: 820px; }

.lc h1, .lc h2, .lc h3 { font-family: 'Plus Jakarta Sans', 'Inter', sans-serif; letter-spacing: -0.02em; }

.lc-grad {
  background: linear-gradient(90deg, #fb923c 0%, #fbbf24 45%, #4ade80 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* ════════════ Buttons ════════════ */
.lc-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  font-weight: 700; border-radius: 999px; text-decoration: none; white-space: nowrap;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  padding: 12px 22px; font-size: 15px; line-height: 1;
}
.lc-btn:hover { transform: translateY(-2px); }
.lc-btn:active { transform: translateY(0); }
.lc-btn:focus-visible { outline: 3px solid var(--amber); outline-offset: 3px; }
.lc-btn i { font-size: 1.15em; }
.lc-btn--sm { padding: 10px 18px; font-size: 14px; }
.lc-btn--lg { padding: 18px 30px; font-size: 17px; }
.lc-btn--xl { padding: 22px 38px; font-size: 19px; }
.lc-btn--block { width: 100%; }
.lc-btn--wa {
  background: linear-gradient(180deg, #2fe073, var(--wa-2));
  color: #fff;
  box-shadow: 0 10px 30px -8px rgba(37, 211, 102, 0.65), inset 0 1px 0 rgba(255,255,255,0.25);
}
.lc-btn--wa:hover { box-shadow: 0 16px 40px -8px rgba(37, 211, 102, 0.8), inset 0 1px 0 rgba(255,255,255,0.25); }
.lc-btn--ghost { color: #fff; border: 1.5px solid rgba(255,255,255,0.25); background: rgba(255,255,255,0.04); }
.lc-btn--ghost:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.45); }
.lc-btn--ghost i { font-size: 0.85em; }

@media (prefers-reduced-motion: no-preference) {
  .lc-btn--pulse { animation: lc-pulse 2.4s ease-out infinite; }
}
@keyframes lc-pulse {
  0% { box-shadow: 0 10px 30px -8px rgba(37,211,102,0.65), 0 0 0 0 rgba(37,211,102,0.55); }
  70% { box-shadow: 0 10px 30px -8px rgba(37,211,102,0.65), 0 0 0 18px rgba(37,211,102,0); }
  100% { box-shadow: 0 10px 30px -8px rgba(37,211,102,0.65), 0 0 0 0 rgba(37,211,102,0); }
}

/* ════════════ Nav ════════════ */
.lc-nav { position: fixed; inset: 0 0 auto 0; z-index: 50; transition: background-color 0.3s ease, box-shadow 0.3s ease; }
.lc-nav__inner { display: flex; align-items: center; justify-content: space-between; height: 72px; gap: 16px; }
.lc-nav__brand img { height: 32px; width: auto; filter: brightness(0) invert(1); transition: filter 0.3s ease; }
.lc-nav__links { display: none; gap: 28px; }
.lc-nav__links a { color: rgba(255,255,255,0.8); font-size: 15px; font-weight: 500; text-decoration: none; transition: color 0.2s; }
.lc-nav__links a:hover { color: #fff; }
.lc-nav.is-scrolled { background: rgba(255,255,255,0.9); -webkit-backdrop-filter: blur(18px); backdrop-filter: blur(18px); box-shadow: 0 1px 0 rgba(0,0,0,0.06); }
.lc-nav.is-scrolled .lc-nav__brand img { filter: none; }
.lc-nav.is-scrolled .lc-nav__links a { color: var(--muted); }
.lc-nav.is-scrolled .lc-nav__links a:hover { color: var(--orange-2); }
@media (min-width: 900px) { .lc-nav__links { display: flex; } }

/* ════════════ Hero ════════════ */
.lc-hero { position: relative; background: var(--ink); color: #fff; padding: 132px 0 96px; overflow: hidden; isolation: isolate; }
.lc-hero__bg { position: absolute; inset: 0; z-index: -1; }
.lc-glow { position: absolute; border-radius: 50%; filter: blur(90px); opacity: 0.55; }
.lc-glow--orange { width: 560px; height: 560px; background: #f97316; top: -180px; left: -160px; opacity: 0.35; }
.lc-glow--green { width: 520px; height: 520px; background: #22c55e; bottom: -220px; right: -120px; opacity: 0.28; }
.lc-hero__grid-pattern {
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 56px 56px;
  -webkit-mask-image: radial-gradient(ellipse at 50% 30%, #000 20%, transparent 75%);
  mask-image: radial-gradient(ellipse at 50% 30%, #000 20%, transparent 75%);
}
.lc-hero__grid { display: grid; gap: 64px; align-items: center; }
@media (min-width: 1024px) { .lc-hero__grid { grid-template-columns: 1.1fr 0.9fr; } }

.lc-pill {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 8px 16px; border-radius: 999px; font-size: 13px; font-weight: 600;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); color: #fed7aa;
}
.lc-pill__dot { width: 8px; height: 8px; border-radius: 50%; background: var(--wa); box-shadow: 0 0 0 4px rgba(37,211,102,0.2); }

.lc-hero__title { font-size: clamp(2.3rem, 5.4vw, 4rem); line-height: 1.05; font-weight: 800; margin: 22px 0 20px; }
.lc-hero__title .lc-grad { display: block; }
.lc-hero__sub { font-size: clamp(1.05rem, 1.6vw, 1.2rem); line-height: 1.65; color: #cbd5e1; max-width: 580px; }
.lc-hero__sub strong { color: #fff; }
.lc-hero__sub em { color: #fde68a; font-style: normal; }

.lc-hero__price {
  display: inline-flex; align-items: baseline; flex-wrap: wrap; gap: 6px 14px;
  margin: 30px 0 28px; padding: 14px 22px; border-radius: 18px;
  background: linear-gradient(135deg, rgba(249,115,22,0.18), rgba(251,191,36,0.08));
  border: 1px solid rgba(251,146,60,0.35);
}
.lc-hero__price-label { font-size: 15px; color: #fed7aa; font-weight: 600; }
.lc-hero__price-value { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 44px; font-weight: 800; line-height: 1; color: #fff; }
.lc-hero__price-note { font-size: 13px; color: #a7f3d0; font-weight: 600; }

.lc-hero__ctas { display: flex; flex-wrap: wrap; gap: 14px; }
.lc-hero__trust { display: flex; flex-wrap: wrap; gap: 10px 22px; margin-top: 28px; padding: 0; list-style: none; color: #94a3b8; font-size: 14px; font-weight: 500; }
.lc-hero__trust i { color: var(--wa); margin-right: 6px; }

@media (max-width: 560px) {
  .lc-hero__ctas .lc-btn { width: 100%; }
  .lc-hero__price { display: flex; }
}

/* Phone mockup */
.lc-hero__visual { position: relative; display: flex; justify-content: center; padding: 20px 0; }
.lc-phone {
  position: relative; width: 300px; padding: 12px; border-radius: 46px;
  background: linear-gradient(160deg, #2a3350, #0f1424);
  box-shadow: var(--shadow-lg), 0 0 0 1px rgba(255,255,255,0.08), inset 0 0 0 2px rgba(255,255,255,0.05);
  transform: rotate(-3deg);
}
@media (prefers-reduced-motion: no-preference) {
  .lc-phone { animation: lc-float 6s ease-in-out infinite; }
  .lc-float--order { animation: lc-float 5s ease-in-out -1s infinite; }
  .lc-float--stock { animation: lc-float 7s ease-in-out -2s infinite; }
}
@keyframes lc-float {
  0%, 100% { translate: 0 0; }
  50% { translate: 0 -12px; }
}
.lc-phone__notch { position: absolute; top: 20px; left: 50%; width: 90px; height: 24px; margin-left: -45px; border-radius: 999px; background: #0b0f1c; z-index: 2; }
.lc-phone__screen { border-radius: 36px; overflow: hidden; background: #f8fafc; color: var(--text); padding-bottom: 14px; }

.lc-mock__bar { display: flex; align-items: center; justify-content: space-between; padding: 44px 14px 12px; background: var(--orange); color: #fff; }
.lc-mock__store { display: flex; align-items: center; gap: 8px; }
.lc-mock__store strong { display: block; font-size: 13px; line-height: 1.2; }
.lc-mock__store small { display: block; font-size: 9px; opacity: 0.85; }
.lc-mock__logo { width: 30px; height: 30px; border-radius: 50%; background: #fff; color: var(--orange-2); font-size: 11px; font-weight: 800; display: grid; place-items: center; }
.lc-mock__bag { position: relative; width: 30px; height: 30px; border-radius: 50%; background: rgba(255,255,255,0.22); display: grid; place-items: center; font-size: 12px; }
.lc-mock__bag b { position: absolute; top: -3px; right: -3px; width: 15px; height: 15px; border-radius: 50%; background: var(--wa); font-size: 9px; display: grid; place-items: center; }
.lc-mock__banner { margin: 12px 12px 0; padding: 14px; border-radius: 14px; background: linear-gradient(120deg, #1e293b, #0f172a); color: #fff; }
.lc-mock__banner span { display: block; font-size: 10px; color: #fdba74; font-weight: 600; }
.lc-mock__banner strong { font-size: 17px; }
.lc-mock__search { margin: 10px 12px 0; padding: 8px 12px; border-radius: 10px; background: #fff; border: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; }
.lc-mock__search i { margin-right: 6px; }
.lc-mock__chips { display: flex; gap: 6px; padding: 10px 12px 4px; overflow: hidden; }
.lc-mock__chips span { flex-shrink: 0; padding: 5px 10px; border-radius: 999px; font-size: 10px; font-weight: 600; background: #fff; border: 1px solid #e2e8f0; color: #64748b; }
.lc-mock__chips .is-active { background: var(--orange); border-color: var(--orange); color: #fff; }
.lc-mock__products { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 8px 12px; }
.lc-mock__card { background: #fff; border-radius: 12px; overflow: hidden; border: 1px solid #f1f5f9; box-shadow: 0 2px 6px rgba(15,23,42,0.05); }
.lc-mock__img { position: relative; height: 66px; display: grid; place-items: center; font-size: 30px; }
.lc-mock__tag { position: absolute; top: 5px; left: 5px; padding: 2px 6px; border-radius: 6px; font-size: 8px; font-weight: 800; font-style: normal; color: #fff; }
.lc-mock__tag--offer { background: #ef4444; }
.lc-mock__tag--featured { background: #10b981; }
.lc-mock__info { padding: 6px 8px 8px; }
.lc-mock__info small { display: block; font-size: 10px; color: #475569; font-weight: 500; }
.lc-mock__info div { display: flex; align-items: center; justify-content: space-between; margin-top: 2px; }
.lc-mock__info b { font-size: 12px; color: var(--orange-2); }
.lc-mock__info i { width: 18px; height: 18px; border-radius: 50%; background: var(--orange); color: #fff; font-size: 8px; display: grid; place-items: center; }
.lc-mock__checkout { margin: 6px 12px 0; padding: 11px; border-radius: 12px; background: var(--wa); color: #fff; font-size: 12px; font-weight: 700; text-align: center; }
.lc-mock__checkout i { margin-right: 4px; }

.lc-float {
  position: absolute; display: flex; align-items: center; gap: 12px;
  padding: 12px 16px; border-radius: 16px; background: rgba(255,255,255,0.96); color: var(--text);
  box-shadow: var(--shadow-md); min-width: 210px;
}
.lc-float strong { display: block; font-size: 13px; }
.lc-float small { display: block; font-size: 11px; color: var(--muted); }
.lc-float__icon { width: 36px; height: 36px; border-radius: 12px; display: grid; place-items: center; color: #fff; font-size: 16px; flex-shrink: 0; }
.lc-float__icon--wa { background: var(--wa); }
.lc-float__icon--orange { background: var(--orange); }
.lc-float--order { top: 70px; right: -10px; }
.lc-float--stock { bottom: 60px; left: -10px; }
@media (max-width: 560px) {
  .lc-float--order { right: 0; top: 30px; }
  .lc-float--stock { left: 0; bottom: 20px; }
  .lc-float { min-width: 0; padding: 10px 12px; }
}
@media (min-width: 1024px) {
  .lc-float--order { right: 0; }
  .lc-float--stock { left: 10px; }
}

/* ════════════ Marquee ════════════ */
.lc-marquee { display: flex; align-items: center; gap: 24px; padding: 22px 0; background: #fff; border-bottom: 1px solid var(--line); overflow: hidden; }
.lc-marquee__label { flex-shrink: 0; padding-left: 20px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: var(--muted); }
.lc-marquee__track { display: flex; overflow: hidden; -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); }
.lc-marquee__list { display: flex; flex-shrink: 0; gap: 12px; margin: 0; padding: 0 12px 0 0; list-style: none; }
.lc-marquee__list li { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 999px; background: var(--bg); border: 1px solid var(--line); font-size: 14px; font-weight: 600; color: #334155; white-space: nowrap; }
@media (prefers-reduced-motion: no-preference) {
  .lc-marquee__list { animation: lc-marquee 38s linear infinite; }
  .lc-marquee__track:hover .lc-marquee__list { animation-play-state: paused; }
}
@keyframes lc-marquee { to { translate: -100% 0; } }

/* ════════════ Sections ════════════ */
.lc-section { padding: 96px 0; }
.lc-section--tint { background: linear-gradient(180deg, var(--tint), #fffbf5); }
.lc-section--dark { background: var(--ink); color: #fff; position: relative; overflow: hidden; }
.lc-section--dark::before { content: ''; position: absolute; width: 600px; height: 600px; border-radius: 50%; background: #22c55e; filter: blur(140px); opacity: 0.16; right: -200px; top: -200px; }

.lc-heading { text-align: center; max-width: 720px; margin: 0 auto 56px; }
.lc-heading h2 { font-size: clamp(1.9rem, 3.6vw, 2.75rem); line-height: 1.12; font-weight: 800; margin: 14px 0 16px; }
.lc-heading p { font-size: 1.1rem; line-height: 1.65; color: var(--muted); }
.lc-eyebrow { display: inline-block; padding: 6px 14px; border-radius: 999px; background: #ffedd5; color: var(--orange-2); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; }
.lc-eyebrow--light { background: rgba(37,211,102,0.14); color: #86efac; }

/* Reveal on scroll (mejora progresiva: sin soporte, el contenido se ve normal) */
@media (prefers-reduced-motion: no-preference) {
  @supports ((animation-timeline: view()) and (animation-range: entry)) {
    .lc-reveal {
      animation: lc-fade-up linear backwards;
      animation-timeline: view();
      animation-range: entry 0% entry 55%;
    }
  }
}
@keyframes lc-fade-up {
  from { opacity: 0; translate: 0 36px; }
}

/* Compare */
.lc-compare { display: grid; gap: 20px; align-items: center; }
@media (min-width: 900px) { .lc-compare { grid-template-columns: 1fr auto 1fr; } }
.lc-compare__card { padding: 32px; border-radius: var(--radius); border: 1px solid var(--line); background: #fff; height: 100%; }
.lc-compare__card h3 { display: flex; align-items: center; gap: 10px; font-size: 1.2rem; font-weight: 800; margin-bottom: 20px; }
.lc-compare__card ul { margin: 0; padding: 0; list-style: none; display: grid; gap: 14px; }
.lc-compare__card li { display: flex; gap: 12px; align-items: flex-start; font-size: 15.5px; line-height: 1.5; color: #334155; }
.lc-compare__card li i { flex-shrink: 0; width: 22px; height: 22px; margin-top: 1px; border-radius: 50%; display: grid; place-items: center; font-size: 10px; }
.lc-compare__card--before h3 { color: #b91c1c; }
.lc-compare__card--before li i { background: #fee2e2; color: #dc2626; }
.lc-compare__card--before li { color: #64748b; }
.lc-compare__card--after { border: 2px solid #86efac; box-shadow: 0 24px 60px -24px rgba(34,197,94,0.45); background: linear-gradient(180deg, #fff, #f0fdf4); }
.lc-compare__card--after h3 { color: #15803d; }
.lc-compare__card--after li i { background: #dcfce7; color: #16a34a; }
.lc-compare__card--after li { font-weight: 500; color: var(--text); }
.lc-compare__arrow { display: none; width: 52px; height: 52px; border-radius: 50%; background: var(--orange); color: #fff; place-items: center; box-shadow: 0 10px 24px -6px rgba(249,115,22,0.6); }
@media (min-width: 900px) { .lc-compare__arrow { display: grid; } }

/* Steps */
.lc-steps { display: grid; gap: 24px; margin: 0; padding: 0; list-style: none; counter-reset: none; }
@media (min-width: 860px) { .lc-steps { grid-template-columns: repeat(3, 1fr); } }
.lc-step { position: relative; padding: 36px 30px 32px; border-radius: var(--radius); background: #fff; border: 1px solid #fde4cc; box-shadow: var(--shadow-md); }
.lc-step__num { position: absolute; top: 18px; right: 22px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 56px; font-weight: 800; line-height: 1; color: #ffedd5; }
.lc-step__icon { display: grid; place-items: center; width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg, var(--orange), #fb923c); color: #fff; font-size: 22px; box-shadow: 0 10px 24px -8px rgba(249,115,22,0.7); margin-bottom: 22px; }
.lc-step h3 { font-size: 1.25rem; font-weight: 800; margin-bottom: 10px; }
.lc-step p { color: var(--muted); line-height: 1.6; }

/* Features */
.lc-features { display: grid; gap: 18px; }
@media (min-width: 640px) { .lc-features { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .lc-features { grid-template-columns: repeat(3, 1fr); } }
.lc-feature { padding: 28px; border-radius: var(--radius); background: #fff; border: 1px solid var(--line); transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease; }
.lc-feature:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); border-color: #fed7aa; }
.lc-feature h3 { font-size: 1.08rem; font-weight: 800; margin: 18px 0 8px; }
.lc-feature p { color: var(--muted); line-height: 1.6; font-size: 15px; }
.lc-feature__icon { display: grid; place-items: center; width: 48px; height: 48px; border-radius: 14px; font-size: 20px; }
.lc-feature__icon--orange { background: #ffedd5; color: #ea580c; }
.lc-feature__icon--green { background: #dcfce7; color: #16a34a; }
.lc-feature__icon--blue { background: #dbeafe; color: #2563eb; }
.lc-feature__icon--purple { background: #ede9fe; color: #7c3aed; }
.lc-feature__icon--pink { background: #fce7f3; color: #db2777; }
.lc-feature__icon--amber { background: #fef3c7; color: #d97706; }

/* Demo WhatsApp */
.lc-demo { position: relative; display: grid; gap: 56px; align-items: center; }
@media (min-width: 960px) { .lc-demo { grid-template-columns: 1fr 1fr; } }
.lc-demo__copy h2 { font-size: clamp(1.9rem, 3.6vw, 2.6rem); line-height: 1.12; font-weight: 800; margin: 14px 0 18px; }
.lc-demo__copy > p { color: #cbd5e1; font-size: 1.08rem; line-height: 1.65; }
.lc-demo__list { margin: 26px 0 34px; padding: 0; list-style: none; display: grid; gap: 12px; }
.lc-demo__list li { display: flex; align-items: center; gap: 12px; font-weight: 500; color: #e2e8f0; }
.lc-demo__list i { width: 24px; height: 24px; border-radius: 50%; background: rgba(37,211,102,0.18); color: var(--wa); display: grid; place-items: center; font-size: 11px; }

.lc-chat { max-width: 420px; width: 100%; margin: 0 auto; border-radius: 24px; overflow: hidden; box-shadow: var(--shadow-lg), 0 0 0 1px rgba(255,255,255,0.08); }
.lc-chat__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; background: var(--wa-dark); color: #fff; }
.lc-chat__head strong { display: block; font-size: 15px; }
.lc-chat__head small { font-size: 12px; opacity: 0.8; }
.lc-chat__avatar { width: 40px; height: 40px; border-radius: 50%; background: #cbd5e1; color: #64748b; display: grid; place-items: center; }
.lc-chat__body {
  padding: 22px 18px 26px;
  background-color: #efeae2;
  background-image: radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px);
  background-size: 14px 14px;
}
.lc-chat__bubble { position: relative; max-width: 92%; margin-left: auto; padding: 12px 14px 22px; border-radius: 14px 4px 14px 14px; background: #d9fdd3; color: #111b21; font-size: 14px; line-height: 1.5; box-shadow: 0 1px 1px rgba(0,0,0,0.08); }
.lc-chat__bubble p + p { margin-top: 8px; }
.lc-chat__sep { color: #64748b; letter-spacing: -1px; }
.lc-chat__time { position: absolute; right: 10px; bottom: 5px; font-size: 11px; color: #667781; }
.lc-chat__time i { color: #53bdeb; margin-left: 2px; }

/* Pricing */
.lc-pricing { position: relative; max-width: 480px; margin: 0 auto; padding: 44px 36px 32px; border-radius: 28px; background: #fff; border: 2px solid var(--orange); box-shadow: 0 40px 90px -30px rgba(249,115,22,0.5); }
.lc-pricing__badge { position: absolute; top: -16px; left: 50%; transform: translateX(-50%); padding: 7px 18px; border-radius: 999px; background: linear-gradient(90deg, var(--orange), #f59e0b); color: #fff; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; white-space: nowrap; box-shadow: 0 8px 20px -6px rgba(249,115,22,0.7); }
.lc-pricing__top { text-align: center; padding-bottom: 26px; border-bottom: 1px dashed var(--line); }
.lc-pricing__top h3 { font-size: 1.3rem; font-weight: 800; }
.lc-pricing__amount { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 88px; font-weight: 800; line-height: 1; margin: 16px 0 6px; color: var(--text); }
.lc-pricing__currency { font-size: 40px; vertical-align: top; margin-right: 4px; position: relative; top: 12px; color: var(--orange-2); }
.lc-pricing__caption { color: var(--muted); font-weight: 500; }
.lc-pricing__anchor { margin-top: 14px; display: inline-block; padding: 6px 12px; border-radius: 10px; background: #f8fafc; font-size: 13px; color: var(--muted); }
.lc-pricing__anchor s { color: #ef4444; }
.lc-pricing__list { margin: 26px 0 30px; padding: 0; list-style: none; display: grid; gap: 13px; }
.lc-pricing__list li { display: flex; align-items: center; gap: 12px; font-size: 15.5px; font-weight: 500; }
.lc-pricing__list i { flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; background: #dcfce7; color: #16a34a; display: grid; place-items: center; font-size: 10px; }
.lc-pricing__foot { margin-top: 16px; text-align: center; font-size: 13px; color: var(--muted); }
.lc-pricing__foot i { color: #16a34a; margin-right: 4px; }
@media (max-width: 480px) { .lc-pricing { padding: 40px 22px 26px; } }

/* FAQ */
.lc-faq { display: grid; gap: 12px; }
.lc-faq__item { border-radius: 16px; background: #fff; border: 1px solid var(--line); transition: border-color 0.2s, box-shadow 0.2s; }
.lc-faq__item[open] { border-color: #fdba74; box-shadow: var(--shadow-md); }
.lc-faq__item summary { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; cursor: pointer; list-style: none; font-weight: 700; font-size: 16.5px; }
.lc-faq__item summary::-webkit-details-marker { display: none; }
.lc-faq__item summary:focus-visible { outline: 3px solid var(--amber); outline-offset: 2px; border-radius: 16px; }
.lc-faq__item summary i { flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%; background: #ffedd5; color: var(--orange-2); display: grid; place-items: center; font-size: 12px; transition: transform 0.25s ease, background-color 0.25s; }
.lc-faq__item[open] summary i { transform: rotate(45deg); background: var(--orange); color: #fff; }
.lc-faq__item p { padding: 0 24px 22px; color: var(--muted); line-height: 1.65; }
.lc-faq__more { margin-top: 32px; text-align: center; color: var(--muted); }
.lc-faq__more a { color: #16a34a; font-weight: 700; text-decoration: none; margin-left: 6px; }
.lc-faq__more a:hover { text-decoration: underline; }

/* Final CTA */
.lc-final { position: relative; padding: 110px 0; background: radial-gradient(ellipse at 20% 0%, rgba(249,115,22,0.35), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(34,197,94,0.3), transparent 55%), var(--ink); color: #fff; text-align: center; overflow: hidden; }
.lc-final__inner { max-width: 820px; }
.lc-final h2 { font-size: clamp(2rem, 4.4vw, 3.2rem); line-height: 1.1; font-weight: 800; margin-bottom: 20px; }
.lc-final p { font-size: 1.15rem; color: #cbd5e1; margin-bottom: 36px; }
.lc-final p strong { color: #fff; }
.lc-final__phone { display: block; margin-top: 18px; color: #94a3b8; font-size: 14px; letter-spacing: 0.04em; }
@media (max-width: 560px) { .lc-final .lc-btn { width: 100%; white-space: normal; } }

/* Footer */
.lc-footer { padding: 32px 0 110px; background: #070b16; color: #64748b; font-size: 14px; }
.lc-footer__inner { display: flex; flex-direction: column; align-items: center; gap: 14px; text-align: center; }
.lc-footer__logo { height: 26px; filter: brightness(0) invert(1); opacity: 0.8; }
.lc-footer span { color: var(--orange); }
.lc-footer nav { display: flex; gap: 20px; }
.lc-footer a { color: #94a3b8; text-decoration: none; }
.lc-footer a:hover { color: #fff; }
@media (min-width: 768px) {
  .lc-footer { padding-bottom: 32px; }
  .lc-footer__inner { flex-direction: row; justify-content: space-between; }
}

/* Floating WhatsApp (desktop) */
.lc-fab { position: fixed; right: 24px; bottom: 24px; z-index: 60; width: 62px; height: 62px; border-radius: 50%; display: none; place-items: center; background: var(--wa); color: #fff; font-size: 32px; text-decoration: none; box-shadow: 0 14px 34px -8px rgba(37,211,102,0.75); transition: transform 0.2s ease; }
.lc-fab:hover { transform: scale(1.08); }
.lc-fab:focus-visible { outline: 3px solid var(--amber); outline-offset: 3px; }
@media (prefers-reduced-motion: no-preference) { .lc-fab { animation: lc-pulse 2.4s ease-out infinite; } }
@media (min-width: 768px) { .lc-fab { display: grid; } }

/* Sticky bar (mobile) */
.lc-mobilebar {
  position: fixed; left: 12px; right: 12px; bottom: 12px; z-index: 60;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 10px 10px 10px 18px; border-radius: 18px;
  background: rgba(11,16,32,0.94); -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px);
  color: #fff; box-shadow: var(--shadow-lg);
  transform: translateY(140%); transition: transform 0.35s ease;
}
.lc-mobilebar.is-visible { transform: translateY(0); }
.lc-mobilebar small { display: block; font-size: 11px; color: #94a3b8; }
.lc-mobilebar strong { font-size: 17px; }
@media (min-width: 768px) { .lc-mobilebar { display: none; } }
</style>
